import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Upload, Download, FileSpreadsheet, Users, CheckCircle, XCircle, 
  AlertTriangle, ArrowLeft, Mail, Eye, EyeOff, Copy, Check
} from 'lucide-react';
import Navbar from '@/components/Navbar';
import { api } from '@/utils/api';
import { useLanguage } from '@/LanguageContext';

const BulkUserUpload = ({ user, onLogout }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [sendEmails, setSendEmails] = useState(true);
  const [showPasswords, setShowPasswords] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (selectedFile) => {
    const validTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    
    if (!validTypes.includes(selectedFile.type) && 
        !selectedFile.name.endsWith('.csv') && 
        !selectedFile.name.endsWith('.xlsx') && 
        !selectedFile.name.endsWith('.xls')) {
      alert('Please upload a CSV or Excel file');
      return;
    }
    
    setFile(selectedFile);
    setResult(null);
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.getUserUploadTemplate();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'frontline_worker_bulk_upload_template.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading template:', error);
      alert('Failed to download template');
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    
    setUploading(true);
    setResult(null);
    
    try {
      const response = await api.bulkUploadUsers(file, sendEmails);
      setResult(response.data);
    } catch (error) {
      console.error('Upload error:', error);
      setResult({
        total: 0,
        successful: 0,
        failed: 0,
        errors: [{ row: 0, error: error.response?.data?.detail || 'Upload failed' }],
        created_users: []
      });
    } finally {
      setUploading(false);
    }
  };

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  return (
    <>
      <div className="min-h-screen bg-[#FAFAF9]" data-testid="bulk-user-upload-page">
        <Navbar user={user} onLogout={onLogout} />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center space-x-2 text-[#4B5563] hover:text-[#0F4C81] mb-4"
              data-testid="back-button"
            >
              <ArrowLeft size={20} />
              <span>Back</span>
            </button>
            
            <div className="flex items-center space-x-4">
              <div className="bg-gradient-to-br from-[#10B981] to-[#059669] p-3 rounded-2xl shadow-lg">
                <Users className="text-white" size={32} />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-[#111827] font-['Nunito']" data-testid="page-title">
                  Bulk Upload Frontline Workers
                </h1>
                <p className="text-base text-[#4B5563]">
                  Upload multiple frontline worker accounts at once using CSV or Excel
                </p>
              </div>
            </div>
          </div>

          {/* Instructions Card */}
          <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-6 mb-8">
            <h3 className="font-bold text-blue-800 mb-3 flex items-center">
              <FileSpreadsheet className="mr-2" size={20} />
              How It Works
            </h3>
            <ol className="list-decimal list-inside space-y-2 text-blue-700 text-sm">
              <li>Download the CSV template using the button below</li>
              <li>Fill in frontline worker details (Name, Email, State, District)</li>
              <li>Upload the completed file</li>
              <li>System creates accounts with temporary passwords</li>
              <li>Workers receive welcome email with login credentials (optional)</li>
            </ol>
          </div>

          {/* Download Template */}
          <div className="bg-white rounded-2xl border-2 border-slate-200 p-6 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-[#111827] mb-1">Step 1: Download Template</h3>
                <p className="text-sm text-[#6B7280]">
                  Get the CSV template with required columns and examples
                </p>
              </div>
              <button
                onClick={handleDownloadTemplate}
                className="flex items-center space-x-2 px-5 py-3 bg-[#0F4C81] text-white rounded-xl font-semibold hover:bg-[#0d3d6b] transition-colors shadow-md"
                data-testid="download-template-button"
              >
                <Download size={20} />
                <span>Download Template</span>
              </button>
            </div>
          </div>

          {/* Upload Section */}
          <div className="bg-white rounded-2xl border-2 border-slate-200 p-6 mb-6">
            <h3 className="font-bold text-[#111827] mb-4">Step 2: Upload File</h3>
            
            {/* Drag & Drop Area */}
            <div
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                dragActive 
                  ? 'border-[#10B981] bg-green-50' 
                  : file 
                    ? 'border-[#10B981] bg-green-50' 
                    : 'border-slate-300 hover:border-[#0F4C81]'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              data-testid="drop-zone"
            >
              {file ? (
                <div className="flex flex-col items-center">
                  <CheckCircle className="text-[#10B981] mb-3" size={48} />
                  <p className="font-semibold text-[#111827]">{file.name}</p>
                  <p className="text-sm text-[#6B7280]">
                    {(file.size / 1024).toFixed(1)} KB
                  </p>
                  <button
                    onClick={() => setFile(null)}
                    className="mt-3 text-sm text-red-600 hover:underline"
                  >
                    Remove file
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <Upload className="text-[#9CA3AF] mb-3" size={48} />
                  <p className="font-semibold text-[#111827]">
                    Drag & drop your file here
                  </p>
                  <p className="text-sm text-[#6B7280] mb-4">or</p>
                  <label className="cursor-pointer">
                    <span className="px-5 py-2 bg-[#0F4C81] text-white rounded-lg font-semibold hover:bg-[#0d3d6b] transition-colors">
                      Browse Files
                    </span>
                    <input
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={(e) => handleFileSelect(e.target.files[0])}
                      className="hidden"
                      data-testid="file-input"
                    />
                  </label>
                  <p className="text-xs text-[#9CA3AF] mt-3">
                    Supported formats: CSV, Excel (.xlsx, .xls)
                  </p>
                </div>
              )}
            </div>

            {/* Email Option */}
            <div className="mt-4 flex items-center justify-between bg-slate-50 rounded-xl p-4">
              <div className="flex items-center space-x-3">
                <Mail className="text-[#0F4C81]" size={20} />
                <div>
                  <p className="font-medium text-[#111827]">Send Welcome Emails</p>
                  <p className="text-xs text-[#6B7280]">
                    Workers will receive an email with their login credentials
                  </p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={sendEmails}
                  onChange={(e) => setSendEmails(e.target.checked)}
                  className="sr-only peer"
                  data-testid="send-emails-toggle"
                />
                <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#10B981]"></div>
              </label>
            </div>

            {/* Upload Button */}
            <button
              onClick={handleUpload}
              disabled={!file || uploading}
              className={`w-full mt-6 flex items-center justify-center space-x-2 px-6 py-4 rounded-xl font-bold text-lg transition-all ${
                file && !uploading
                  ? 'bg-[#10B981] text-white hover:bg-[#059669] shadow-lg'
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              }`}
              data-testid="upload-button"
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  <span>Uploading...</span>
                </>
              ) : (
                <>
                  <Upload size={24} />
                  <span>Upload & Create Accounts</span>
                </>
              )}
            </button>
          </div>

          {/* Results */}
          {result && (
            <div className="bg-white rounded-2xl border-2 border-slate-200 p-6" data-testid="upload-results">
              <h3 className="font-bold text-[#111827] mb-4">Upload Results</h3>
              
              {/* Summary Stats */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-slate-50 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-[#111827]">{result.total}</p>
                  <p className="text-sm text-[#6B7280]">Total Rows</p>
                </div>
                <div className="bg-green-50 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-[#10B981]">{result.successful}</p>
                  <p className="text-sm text-[#065F46]">Successful</p>
                </div>
                <div className={`rounded-xl p-4 text-center ${result.failed > 0 ? 'bg-red-50' : 'bg-slate-50'}`}>
                  <p className={`text-3xl font-bold ${result.failed > 0 ? 'text-red-600' : 'text-[#111827]'}`}>
                    {result.failed}
                  </p>
                  <p className={`text-sm ${result.failed > 0 ? 'text-red-700' : 'text-[#6B7280]'}`}>Failed</p>
                </div>
              </div>

              {/* Created Users with Credentials */}
              {result.created_users && result.created_users.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-[#111827] flex items-center">
                      <CheckCircle className="text-[#10B981] mr-2" size={18} />
                      Created Accounts ({result.created_users.length})
                    </h4>
                    <button
                      onClick={() => setShowPasswords(!showPasswords)}
                      className="flex items-center space-x-1 text-sm text-[#0F4C81] hover:underline"
                    >
                      {showPasswords ? <EyeOff size={16} /> : <Eye size={16} />}
                      <span>{showPasswords ? 'Hide' : 'Show'} Passwords</span>
                    </button>
                  </div>
                  
                  <div className="bg-green-50 rounded-xl border border-green-200 overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-green-100">
                        <tr>
                          <th className="text-left py-2 px-4 font-semibold text-[#065F46]">Email</th>
                          <th className="text-left py-2 px-4 font-semibold text-[#065F46]">Temporary Password</th>
                          <th className="text-center py-2 px-4 font-semibold text-[#065F46]">Copy</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.created_users.map((user, index) => (
                          <tr key={index} className="border-t border-green-200">
                            <td className="py-2 px-4 text-[#111827]">{user.email}</td>
                            <td className="py-2 px-4 font-mono">
                              {showPasswords ? user.temp_password : '••••••••••'}
                            </td>
                            <td className="py-2 px-4 text-center">
                              <button
                                onClick={() => copyToClipboard(`${user.email} / ${user.temp_password}`, index)}
                                className="p-1 hover:bg-green-200 rounded"
                                title="Copy credentials"
                              >
                                {copiedIndex === index ? (
                                  <Check className="text-[#10B981]" size={16} />
                                ) : (
                                  <Copy className="text-[#6B7280]" size={16} />
                                )}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {!sendEmails && (
                    <p className="text-sm text-amber-600 mt-2 flex items-center">
                      <AlertTriangle size={14} className="mr-1" />
                      Welcome emails were not sent. Please share credentials manually.
                    </p>
                  )}
                </div>
              )}

              {/* Errors */}
              {result.errors && result.errors.length > 0 && (
                <div>
                  <h4 className="font-semibold text-red-700 mb-3 flex items-center">
                    <XCircle className="mr-2" size={18} />
                    Errors ({result.errors.length})
                  </h4>
                  <div className="bg-red-50 rounded-xl border border-red-200 max-h-60 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-red-100 sticky top-0">
                        <tr>
                          <th className="text-left py-2 px-4 font-semibold text-red-800">Row</th>
                          <th className="text-left py-2 px-4 font-semibold text-red-800">Email</th>
                          <th className="text-left py-2 px-4 font-semibold text-red-800">Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.errors.map((error, index) => (
                          <tr key={index} className="border-t border-red-200">
                            <td className="py-2 px-4 text-red-700">{error.row}</td>
                            <td className="py-2 px-4 text-red-700">{error.email || '-'}</td>
                            <td className="py-2 px-4 text-red-700">{error.error}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Copyright Footer */}
      <footer className="bg-[#F3F4F6] border-t border-gray-200 py-3 text-center">
        <p className="text-sm text-[#6B7280]">© 2025 Hamrith Consulting Services LLP (HCS). All Rights Reserved.</p>
      </footer>
    </>
  );
};

export default BulkUserUpload;
